import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/app/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { CalendarPlus, Trash2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

interface CustomCalendarProps {
  onCalendarCreated?: () => void;
}

interface CustomCalendar {
  id: string;
  name: string;
  color: string;
  created_at: string;
}

// Available color options
const colorOptions = [
  { value: "bg-blue-100 text-blue-800", label: "Blue", class: "bg-blue-100 text-blue-800" },
  { value: "bg-red-100 text-red-800", label: "Red", class: "bg-red-100 text-red-800" },
  { value: "bg-green-100 text-green-800", label: "Green", class: "bg-green-100 text-green-800" },
  { value: "bg-yellow-100 text-yellow-800", label: "Yellow", class: "bg-yellow-100 text-yellow-800" },
  { value: "bg-purple-100 text-purple-800", label: "Purple", class: "bg-purple-100 text-purple-800" },
  { value: "bg-pink-100 text-pink-800", label: "Pink", class: "bg-pink-100 text-pink-800" },
  { value: "bg-indigo-100 text-indigo-800", label: "Indigo", class: "bg-indigo-100 text-indigo-800" },
  { value: "bg-gray-100 text-gray-800", label: "Gray", class: "bg-gray-100 text-gray-800" },
];

const CustomCalendarDialog = ({ onCalendarCreated }: CustomCalendarProps) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [color, setColor] = useState("bg-blue-100 text-blue-800");
  const [loading, setLoading] = useState(false);
  const [userCalendars, setUserCalendars] = useState<CustomCalendar[]>([]);
  const { user } = useAuth();
  const { toast } = useToast();
  const [calendarCount, setCalendarCount] = useState(0);
  const isPro = user?.plan === "pro";
  const maxCalendars = isPro ? 3 : 0;
  const [calendarToDelete, setCalendarToDelete] = useState<{id: string, name: string} | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  
  console.log("CustomCalendarDialog - isPro:", isPro, "user plan:", user?.plan);
  
  // Fetch existing custom calendars
  useEffect(() => {
    const fetchUserCalendars = async () => {
      if (!user) return;
      
      try {
        console.log("Fetching custom calendars for user:", user.id);
        const { data, error } = await supabase
          .from('ft_custom_calendars')
          .select('*')
          .eq('user_id', user.id);
          
        if (error) {
          console.error("Error fetching custom calendars:", error);
          return;
        }
        
        if (data) {
          console.log("Found custom calendars:", data);
          setUserCalendars(data);
          setCalendarCount(data.length);
        }
      } catch (err) {
        console.error("Error fetching custom calendars:", err);
      }
    };
    
    fetchUserCalendars();
  }, [user, open]); // Refresh when dialog opens
  
  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setName("");
      setColor("bg-blue-100 text-blue-800");
    }
  }, [open]);
  
  const handleCreateCalendar = async () => {
    if (!user) {
      toast({
        title: "Error",
        description: "You must be logged in to create a custom calendar",
        variant: "destructive"
      });
      return;
    }
    
    if (!isPro) {
      toast({
        title: "Pro feature",
        description: "Custom calendars are only available for PRO users. Please upgrade your plan.",
        variant: "destructive"
      });
      return;
    }
    
    if (calendarCount >= maxCalendars) {
      toast({
        title: "Calendar limit reached",
        description: "You've reached your maximum number of custom calendars (3).",
        variant: "destructive"
      });
      return;
    }
    
    if (!name.trim()) {
      toast({
        title: "Error",
        description: "Please provide a name for your calendar",
        variant: "destructive"
      });
      return;
    }
    
    setLoading(true);
    
    try {
      console.log("Creating custom calendar with data:", {
        user_id: user.id,
        name: name.trim(),
        color
      });
      
      const { data, error } = await supabase
        .from('ft_custom_calendars')
        .insert({
          user_id: user.id,
          name: name.trim(),
          color
        })
        .select();
        
      if (error) {
        throw error;
      }
      
      if (data && data.length > 0) {
        setUserCalendars([...userCalendars, data[0]]);
        setCalendarCount(calendarCount + 1);
        
        toast({
          title: "Calendar created",
          description: `${name} calendar has been created successfully`,
        });
        
        if (onCalendarCreated) {
          onCalendarCreated();
        }
      }
      
      setOpen(false);
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Failed to create calendar",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  
  const confirmDeleteCalendar = (id: string, calendarName: string) => {
    setCalendarToDelete({ id, name: calendarName });
    setConfirmDialogOpen(true);
  };
  
  const handleDeleteCalendar = async () => {
    if (!user || !calendarToDelete) return;
    
    setLoading(true);
    
    try {
      // First, update any events using this calendar to use "My Calendar" (null group_id)
      const { error: eventUpdateError } = await supabase
        .from('ft_events')
        .update({ group_id: null })
        .eq('group_id', calendarToDelete.id);
        
      if (eventUpdateError) {
        console.error("Error updating events:", eventUpdateError);
        // Continue with deletion anyway
      }
      
      // Now delete the calendar
      const { error } = await supabase
        .from('ft_custom_calendars')
        .delete()
        .eq('id', calendarToDelete.id)
        .eq('user_id', user.id);
        
      if (error) {
        throw error;
      }
      
      // Update local state
      const updatedCalendars = userCalendars.filter(cal => cal.id !== calendarToDelete.id);
      setUserCalendars(updatedCalendars);
      setCalendarCount(updatedCalendars.length);
      
      toast({
        title: "Calendar deleted",
        description: `${calendarToDelete.name} has been deleted`,
      });
      
      if (onCalendarCreated) {
        onCalendarCreated();
      }
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Failed to delete calendar",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
      setConfirmDialogOpen(false);
      setCalendarToDelete(null);
    }
  };
  
  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" className="bg-blue-50 hover:bg-blue-100">
            <CalendarPlus className="mr-2 h-4 w-4" />
            {isPro ? "Manage Custom Calendars" : "Custom Calendars (PRO)"}
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-black">Custom Calendars</DialogTitle>
            <DialogDescription>
              {isPro 
                ? `Create up to ${maxCalendars} custom calendars (${calendarCount}/${maxCalendars} used).` 
                : "Upgrade to PRO to create custom calendars."}
            </DialogDescription>
          </DialogHeader>
          
          {/* Existing calendars list */}
          {userCalendars.length > 0 && (
            <div className="space-y-2 mb-4">
              <h3 className="text-sm font-medium text-black">Your Calendars</h3>
              {userCalendars.map(calendar => (
                <div key={calendar.id} className="flex items-center justify-between p-2 border rounded-md">
                  <div className="flex items-center gap-2">
                    <Badge className={calendar.color}>
                      •
                    </Badge>
                    <span className="text-black">{calendar.name}</span>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => confirmDeleteCalendar(calendar.id, calendar.name)}
                    disabled={loading}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              ))}
            </div>
          )}
          
          {/* Create new calendar form */}
          {isPro && calendarCount < maxCalendars && (
            <>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="calendar-name" className="text-black">Calendar Name</Label>
                  <Input
                    id="calendar-name"
                    placeholder="Work, School, Personal, etc."
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="calendar-color" className="text-black">Color</Label>
                  <Select value={color} onValueChange={setColor}>
                    <SelectTrigger id="calendar-color">
                      <SelectValue placeholder="Select a color" />
                    </SelectTrigger>
                    <SelectContent>
                      {colorOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          <div className="flex items-center gap-2">
                            <Badge className={option.class}>•</Badge>
                            <span className="text-black">{option.label}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <DialogFooter>
                <Button 
                  type="submit" 
                  onClick={handleCreateCalendar} 
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {loading ? "Creating..." : "Create Calendar"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Confirmation Dialog for Calendar Deletion */}
      <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Calendar</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the "{calendarToDelete?.name}" calendar? 
              All events in this calendar will be moved to "My Calendar".
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setCalendarToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteCalendar}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default CustomCalendarDialog;
